import { reportLaunchDarklyBrowserIncident } from '@/client/observability/launchdarkly-browser';
import type {
    BrowserDispatchedIncident,
    IncidentSender,
    IncidentSenderResult,
} from '@/shared/observability/incident-dispatch';

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const pruneUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null));
};

const toLaunchDarklyMetadata = (incident: BrowserDispatchedIncident) => {
    const metadataRecord = isObjectRecord(incident.metadata) ? incident.metadata : null;
    const metadata = pruneUndefined({
        kind: metadataRecord?.kind,
        route: metadataRecord?.route,
        boundary: metadataRecord?.boundary,
        capability: metadataRecord?.capability,
        layer: metadataRecord?.layer,
        operation: incident.operation ?? metadataRecord?.operation,
        boundaryDisposition: metadataRecord?.boundaryDisposition ?? incident.disposition,
    });

    return Object.keys(metadata).length > 0 ? metadata : undefined;
};

export const browserIncidentLaunchDarklySender: IncidentSender<BrowserDispatchedIncident, 'browser_launchdarkly'> = {
    sink: 'browser_launchdarkly',
    send: async (incident): Promise<IncidentSenderResult> => {
        const delivered = reportLaunchDarklyBrowserIncident({
            payload: pruneUndefined({
                message: incident.message,
                errorCode: incident.errorCode,
                errorCategory: incident.errorCategory,
                requestId: incident.requestId,
                correlationId: incident.correlationId,
                operation: incident.operation,
                clientId: incident.clientId,
                platform: incident.platform,
                fingerprint: incident.fingerprint,
                ...(incident.metadata ?? {}),
            }),
            metadata: toLaunchDarklyMetadata(incident),
            fingerprint: incident.fingerprint,
            suppressionWindowMs:
                incident.metadata && 'suppressionWindowMs' in incident.metadata
                    ? typeof incident.metadata.suppressionWindowMs === 'number'
                        ? incident.metadata.suppressionWindowMs
                        : undefined
                    : undefined,
        });

        return {
            delivered,
            reason: delivered ? 'delivered' : 'skipped',
        };
    },
};
